/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { motion } from "framer-motion";
import Head from "next/head";
import Link from "next/link";
import { Component } from "react";

import Banner from "../components/banner";
import Newsletter from "../components/newsletter";
import OpenGraphHead from "../components/og-head";
import TwitterHead from "../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class Purpose extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/purpose", market);
    const alt = "Lasting Smiles";
    const title = "Causes · Lasting Smiles";
    const description = "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/purpose", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/purpose", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/purpose", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="purpose"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="purpose"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-20 sm:px-5"
          style={{
            backgroundImage: `url("${require("../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h1
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h1>
            <p className="font-bold font-serif sm:text-4xl text-2xl text-center text-purpose">
              Caring for People and Our Planet
            </p>
          </div>
        </section>
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Unprecedented times call for unprecedented care.
            </h2>
            <p className="text-center text-xl">
              Pick a product, smack your lips and smile your heart out.
              <br />
              Because your purchase makes a difference.
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop Lip Care
                </a>
              </Link>
            </div>
            <p className="text-center text-xl">
              20% of your purchase is donated to the cause you select at checkout.
              Click on each logo to learn about our cause partners.
            </p>
          </div>
          <div className="max-w-screen-lg mx-auto">
            <div className="flex flex-row flex-wrap items-center justify-around">
              <Link
                as={toRelativePageURL("/partners/bf-best-friends-animal-society", market)}
                href={{
                  pathname: "/partners/bf",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#A6B7FF" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/bf-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/bf-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Best Friends Animal Society"
                      src={require("../public/images/logos/bf-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/cca-childrens-craniofacial-association", market)}
                href={{
                  pathname: "/partners/cca",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#9FDBEA" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/cca-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/cca-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Craniofacial Association"
                      src={require("../public/images/logos/cca-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/cdf-childrens-defense-fund", market)}
                href={{
                  pathname: "/partners/cdf",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#73C489" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/cdf-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/cdf-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Defense Fund"
                      src={require("../public/images/logos/cdf-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/girls-inc", market)}
                href={{
                  pathname: "/partners/girls-inc",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#F4B227" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/girls-inc-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/girls-inc-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls Inc."
                      src={require("../public/images/logos/girls-inc-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/gotr-girls-on-the-run", market)}
                href={{
                  pathname: "/partners/gotr",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#FBCBC8" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/gotr-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/gotr-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls on the Run"
                      src={require("../public/images/logos/gotr-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/i100-impact100-global", market)}
                href={{
                  pathname: "/partners/i100",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#CBB0F4" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/i100-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/i100-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Impact100 Global"
                      src={require("../public/images/logos/i100-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/mow-meals-on-wheels-america", market)}
                href={{
                  pathname: "/partners/mow",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#FFE49D" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/mow-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/mow-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Meals on Wheels America"
                      src={require("../public/images/logos/mow-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/nami-national-alliance-on-mental-illness", market)}
                href={{
                  pathname: "/partners/nami",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#CCE9E2" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/nami-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/nami-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="National Alliance on Mental Illness"
                      src={require("../public/images/logos/nami-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/rmhc-ronald-mcdonald-house-charities-bay-area", market)}
                href={{
                  pathname: "/partners/rmhc-bay-area",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#F47C53" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/rmhc-bay-area-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/rmhc-bay-area-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Ronald McDonald House Charities Bay Area"
                      src={require("../public/images/logos/rmhc-bay-area-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/wfw-women-for-water", market)}
                href={{
                  pathname: "/partners/wfw",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#9FDBEA" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/wfw-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/wfw-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="women for water"
                      src={require("../public/images/logos/wfw-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/wv-world-vision", market)}
                href={{
                  pathname: "/partners/wv",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#BDD457" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/wv-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/wv-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="World Vision"
                      src={require("../public/images/logos/wv-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <Link
                as={toRelativePageURL("/partners/ya-young-audiences-arts-for-learning", market)}
                href={{
                  pathname: "/partners/ya",
                  query: { market: "en-US" },
                }}
              >
                <motion.a
                  className="border-8 cursor-pointer flex-initial m-5 overflow-hidden rounded-full"
                  style={{ borderColor: "#F6A4AE" }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <picture>
                    <source
                      srcSet={require("../public/images/logos/ya-square.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/logos/ya-square.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Young Audiences Arts for Learning"
                      src={require("../public/images/logos/ya-square.jpg") as string}
                      style={{
                        maxHeight: 200,
                        maxWidth: 200,
                      }}
                    />
                  </picture>
                  <span>{" "}</span>
                </motion.a>
              </Link>
              <div className="flex-initial m-5">
                <span className="font-medium font-serif text-xl">More partnerships to come!</span>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}
